import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import zxcvbn from 'zxcvbn';

export type PasswordStrengthLevel = 'weak' | 'acceptable' | 'good' | 'strong';

/** Zelfde beoordeling als de server: zxcvbn-score >= 2 is vereist ("aanvaardbaar"). */
export const MINIMUM_PASSWORD_SCORE = 2;
export const MINIMUM_PASSWORD_LENGTH = 8;
/** Vanaf deze lengte mag een score-3 wachtwoord als "sterk" tonen (anders "goed"). */
export const STRONG_PASSWORD_LENGTH = 14;

export function passwordScore(password: string): number {
  return zxcvbn(password).score;
}

export function isPasswordAcceptable(password: string): boolean {
  return password.length >= MINIMUM_PASSWORD_LENGTH && passwordScore(password) >= MINIMUM_PASSWORD_SCORE;
}

/**
 * Wachtwoordsterkte-meter (zwak / aanvaardbaar / goed / sterk) op basis van zxcvbn —
 * dezelfde acceptatiedrempel als de backend (score >= 2) zodat client en server
 * nooit verschillen; "goed" en "sterk" zijn puur visuele nuances erboven.
 */
@Component({
  selector: 'password-strength-meter',
  imports: [TranslocoModule],
  templateUrl: './password-strength-meter.component.html',
  styleUrl: './password-strength-meter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasswordStrengthMeterComponent {
  readonly password = input.required<string>();

  protected readonly score = computed(() => passwordScore(this.password()));

  protected readonly level = computed<PasswordStrengthLevel>(() => {
    const score = this.score();
    if (this.isStrong(score)) {
      return 'strong';
    }
    if (score >= 3) {
      return 'good';
    }
    if (score >= MINIMUM_PASSWORD_SCORE) {
      return 'acceptable';
    }
    return 'weak';
  });

  protected readonly isAcceptable = computed(() => isPasswordAcceptable(this.password()));

  protected readonly segments = computed(() => {
    const filled = this.filledSegmentsFor(this.level());
    return [1, 2, 3, 4].map(index => index <= filled);
  });

  private isStrong(score: number): boolean {
    return score >= 4 || (score >= 3 && this.password().length >= STRONG_PASSWORD_LENGTH);
  }

  private filledSegmentsFor(level: PasswordStrengthLevel): number {
    switch (level) {
      case 'strong':
        return 4;
      case 'good':
        return 3;
      case 'acceptable':
        return 2;
      default:
        return this.password() ? 1 : 0;
    }
  }
}
